import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import attendanceService from '../../../services/attendanceService';

const EditDayAttendanceModal = ({
  isOpen,
  onClose,
  employee,
  dayData,
  onSaved
}) => {
  const [status, setStatus] = useState('present');
  const [checkInTime, setCheckInTime] = useState('');
  const [checkOutTime, setCheckOutTime] = useState('');
  const [workHours, setWorkHours] = useState('');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (dayData) {
      setStatus(dayData.status || 'present');
      setCheckInTime(dayData.check_in_time ? dayData.check_in_time.split(' ')[1] || dayData.check_in_time : '09:00');
      setCheckOutTime(dayData.check_out_time ? dayData.check_out_time.split(' ')[1] || dayData.check_out_time : '18:00');
      setWorkHours(dayData.worked_hours !== null && dayData.worked_hours !== undefined ? String(dayData.worked_hours) : '8.0');
      setRemarks(dayData.remarks || '');
      setError(null);
    }
  }, [dayData, isOpen]);

  if (!isOpen || !employee || !dayData) return null;

  const statusOptions = [
    { value: 'present', label: 'Present' },
    { value: 'absent', label: 'Absent' },
    { value: 'leave', label: 'Leave (L)' },
    { value: 'paid_leave', label: 'Paid Leave (PL)' },
    { value: 'week_off', label: 'Week Off (WO)' },
    { value: 'half_day', label: 'Half Day (HD)' },
    { value: 'late', label: 'Late Arrival' }
  ];

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);

      const targetStatus = typeof status === 'object' ? status?.value || status?.target?.value : status;

      const payload = {
        employee_id: employee.id,
        date: dayData.date,
        status: targetStatus,
        check_in_time: (targetStatus === 'present' || targetStatus === 'late' || targetStatus === 'half_day') ? checkInTime : null,
        check_out_time: (targetStatus === 'present' || targetStatus === 'late' || targetStatus === 'half_day') ? checkOutTime : null,
        worked_hours: (targetStatus === 'present' || targetStatus === 'late' || targetStatus === 'half_day') ? parseFloat(workHours || 0) : null,
        remarks: remarks
      };

      console.log('[EditDayAttendanceModal] Saving payload:', payload);

      const res = await attendanceService.saveAttendanceRecord(payload);
      if (res.success) {
        if (onSaved) onSaved(res.data);
        onClose();
      } else {
        setError(res.error || 'Failed to save attendance record.');
      }
    } catch (err) {
      setError(err.message || 'Error updating record.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Modal Header */}
        <div className="p-5 border-b border-border flex items-center justify-between bg-muted/30">
          <div>
            <h3 className="text-base font-bold text-foreground">Edit Daily Attendance</h3>
            <p className="text-xs text-muted-foreground">
              {employee.full_name} • <span className="font-semibold text-primary">{dayData.date} ({dayData.weekday})</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSave} className="p-5 space-y-4 text-sm">
          {error && (
            <div className="p-3 bg-rose-500/10 border border-rose-500/20 text-rose-600 rounded-md text-xs font-medium">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">Attendance Status</label>
            <Select
              options={statusOptions}
              value={status}
              onChange={(val) => setStatus(typeof val === 'object' ? val?.target?.value || val?.value || 'present' : val)}
              placeholder="Select status"
            />
          </div>

          {(status === 'present' || status === 'late' || status === 'half_day') && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Check-In Time</label>
                  <Input
                    type="time"
                    value={checkInTime}
                    onChange={(e) => setCheckInTime(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Check-Out Time</label>
                  <Input
                    type="time"
                    value={checkOutTime}
                    onChange={(e) => setCheckOutTime(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Worked Hours (DECIMAL)</label>
                <Input
                  type="number"
                  step="0.25"
                  min="0"
                  max="24"
                  value={workHours}
                  onChange={(e) => setWorkHours(e.target.value)}
                  placeholder="e.g. 8.5"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">Manager Remarks / Reason</label>
            <textarea
              className="w-full px-3 py-2 bg-background border border-input rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-primary text-foreground"
              rows={2}
              placeholder="Reason for manual adjustment..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>

          <div className="pt-3 border-t border-border flex items-center justify-end gap-3">
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} iconName={saving ? 'Loader2' : 'Check'}>
              {saving ? 'Saving...' : 'Save Record'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDayAttendanceModal;
